import { useEffect, useState, useReducer, useContext } from "react";
import { useNavigate } from "react-router-dom";

import AppContext from "../../../store/app-context";
import AuthContext from "../../../store/auth-context";
import { getEffectiveRoles } from "../../../config/roles";

import './Login.scss';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { RiSmartphoneLine, RiComputerLine } from 'react-icons/ri';

type loginProps = {};

type AppMode = 'scanner' | 'dashboard';

interface FormState {
    value: string;
    isValid: boolean;
}

const emailReducer = (state: FormState, action: any): FormState => {
    if (action.type === 'input') {
        return {
            value: action.value,
            isValid: action.value.length > 0 && action.value.includes('@') && action.value.split('@')[1].length > 0
        };
    }

    return { value: '', isValid: false };
};

const passwordReducer = (state: FormState, action: any): FormState => {
    if (action.type === 'input') {
        return {
            value: action.value,
            isValid: action.value.length > 6
        };
    }

    return { value: '', isValid: false };
};

const getDefaultMode = (): AppMode => {
    const stored = localStorage.getItem('app_mode') as AppMode | null;
    if (stored) return stored;
    // Auto-detect: desktop → dashboard, mobile/touch → scanner
    const isDesktop = window.matchMedia('(min-width: 800px) and (hover: hover)').matches;
    return isDesktop ? 'dashboard' : 'scanner';
};

const Login = (props: loginProps) => {
    const appCtx = useContext(AppContext);
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();

    const [validForm, setValidForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [mode, setMode] = useState<AppMode>(getDefaultMode);

    // Track auth transition: only redirect on fresh login, not on page load with existing session

    const [emailState, dispatchEmail] = useReducer(emailReducer, {
        value: '',
        isValid: false
    });

    const [passwordState, dispatchPassword] = useReducer(passwordReducer, {
        value: '',
        isValid: false
    });

    const emailChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        authCtx.clearDeactivatedMessage();
        dispatchEmail({
            type: 'input',
            value: event.target.value
        });
    };

    const passwordChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        authCtx.clearDeactivatedMessage();
        dispatchPassword({
            type: 'input',
            value: event.target.value
        });
    };

    const toggleShowPassword = () => {
        setShowPassword(prevShowPassword => !prevShowPassword);
    };

    const handleModeChange = (newMode: AppMode) => {
        setMode(newMode);
        localStorage.setItem('app_mode', newMode);
    };

    useEffect(() => {
        setValidForm(emailState.isValid && passwordState.isValid);
    }, [emailState.isValid, passwordState.isValid]);

    // Post-login redirect based on mode + role (only on fresh login, not session restore)
    useEffect(() => {
        if (authCtx.justLoggedIn && authCtx.isAuthenticated && authCtx.user) {
            authCtx.clearJustLoggedIn();
            const roles = getEffectiveRoles(authCtx.user.role);

            if (mode === 'dashboard') {
                if (authCtx.dashboardEnabled) {
                    // Redirect to appropriate dashboard based on role
                    if (roles.includes('ADMIN') || roles.includes('COURIER')) {
                        navigate('/dashboard/partner', { replace: true });
                    } else if (roles.includes('WAREHOUSE')) {
                        navigate('/dashboard/warehouse', { replace: true });
                    } else {
                        navigate('/', { replace: true });
                    }
                } else {
                    // User selected dashboard but doesn't have access — redirect to scanner with info
                    navigate('/', { replace: true });
                    appCtx.newMessage({ text: 'Dashboard access is not enabled for your account. Contact your administrator.', type: 2 });
                }
            } else {
                navigate('/', { replace: true });
            }
        }
    }, [authCtx.isAuthenticated, authCtx.justLoggedIn, authCtx.dashboardEnabled, authCtx.user, authCtx, appCtx, mode, navigate]);

    const submitLogin = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Trimmed and lowercased before it leaves the device. The backend matches
        // case-insensitively as of 2026-09-26, so this is not what makes sign-in work —
        // it is so the address shown in an error, stored in a password manager or read
        // back off the wire is the one the account actually has. A phone is where a
        // stray leading space and a capitalised first letter come from.
        authCtx.sendLogIn({
            email: emailState.value.trim().toLowerCase(),
            password: passwordState.value
        });
    };

    // Check if user is authenticated
    const isAuthenticated = authCtx.isAuthenticated;

    return (
        <>
            {appCtx.init && (
                <div id="login" className={isAuthenticated ? 'hide' : ''}>
                    <div className="login-container">
                        {/* Left branding panel — visible on desktop only */}
                        <div className="login-hero">
                            <div className="hero-content">
                                <img src="/kit-keeper-logo.svg" alt="Kit Keeper logo" className="hero-logo" />
                                <h1>Kit Keeper</h1>
                                <p className="hero-tagline">Logistics Management Platform</p>
                                <div className="hero-features">
                                    <div className="feature">
                                        <RiSmartphoneLine />
                                        <div>
                                            <strong>Scanner App</strong>
                                            <span>Mobile barcode scanning, route management, and real-time deliveries</span>
                                        </div>
                                    </div>
                                    <div className="feature">
                                        <RiComputerLine />
                                        <div>
                                            <strong>Dashboard</strong>
                                            <span>Analytics, forecasting, invoices, and warehouse operations</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right form panel */}
                        <div className="login-card">
                            <div className="login-brand">
                                <img src="/kit-keeper-logo.svg" alt="Kit Keeper logo" />
                                <h1>Logistics App</h1>
                                <h2>Sign in to your account</h2>
                            </div>

                            <div className="mode-toggle-wrapper">
                                <div className="mode-toggle">
                                    <button
                                        type="button"
                                        className={`mode-option ${mode === 'scanner' ? 'active' : ''}`}
                                        onClick={() => handleModeChange('scanner')}
                                    >
                                        <RiSmartphoneLine />
                                        <div className="mode-text">
                                            <span className="mode-label">Scanner App</span>
                                            <span className="mode-desc">Mobile scanning</span>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        className={`mode-option ${mode === 'dashboard' ? 'active' : ''}`}
                                        onClick={() => handleModeChange('dashboard')}
                                    >
                                        <RiComputerLine />
                                        <div className="mode-text">
                                            <span className="mode-label">Dashboard</span>
                                            <span className="mode-desc">Desktop analytics</span>
                                        </div>
                                    </button>
                                </div>
                                <p className={`mode-hint ${mode === 'dashboard' ? 'visible' : ''}`}>
                                    <FaInfoCircle /> Dashboard access is enabled per account
                                </p>
                            </div>

                            {authCtx.deactivatedMessage && (
                                <div className="account-deactivated" role="alert" aria-live="assertive">
                                    <FaExclamationTriangle aria-hidden="true" />
                                    <div>
                                        <strong>This account has been deactivated</strong>
                                        <p>{authCtx.deactivatedMessage}</p>
                                    </div>
                                </div>
                            )}

                            <form autoComplete="off" onSubmit={submitLogin}>
                                <div className="form-control">
                                    <label>Email</label>
                                    <div className="input-wrapper">
                                        <FaEnvelope className="input-icon" />
                                        <input
                                            type="email"
                                            value={emailState.value}
                                            onChange={emailChanged}
                                            autoComplete="off"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            inputMode="email"
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                </div>
                                <div className="form-control">
                                    <label>
                                        Password
                                        <span className="show-password" onClick={toggleShowPassword}>
                                            {showPassword && <FaEyeSlash />}
                                            {!showPassword && <FaEye />}
                                            {showPassword ? 'hide' : 'show'}
                                        </span>
                                    </label>
                                    <div className="input-wrapper">
                                        <FaLock className="input-icon" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={passwordState.value}
                                            onChange={passwordChanged}
                                            autoComplete="off"
                                            placeholder="Enter your password"
                                        />
                                    </div>
                                </div>
                                <button type="submit" disabled={!validForm}>Sign In</button>
                            </form>
                            <p className="forgot-password"><span className="link">Forgot your password?</span></p>

                            {!appCtx.isOnline && (
                                <div className="offline-warning">
                                    <p><FaExclamationTriangle /> You are offline. Login requires an internet connection.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;
